import React, {useCallback, useState} from "react";
import {Button, Col, Form, Input, message, Modal, Popconfirm, Popover, Row, Space, Table} from "antd";
import HierarchyNode from "../../ImageViewerHandlers/Hierarchy/HierarchyNode";
import {ColumnsType} from "antd/es/table";
import {EditableTextField} from "../../EditableFields/EditableTextField";
import {DeleteOutlined} from "@ant-design/icons";

export function NodeAliasesEditor({node, visible, onVisibleChange}: {
    node: HierarchyNode,
    visible: boolean,
    onVisibleChange: (visible: boolean) => void,
}) {
    const saveAliases = useCallback((aliases: string[]) => {
        node.updateAliases(
            aliases
        ).then(() => {
            onVisibleChange(false);
            message.success("Saved");
        }).catch(() => {
            message.error("Failed to save aliases!");
        });
    }, [node]);

    return (
        <Modal
            title={"Manage Aliases"}
            visible={visible}
            onCancel={() => onVisibleChange(false)}
            destroyOnClose={true}
            footer={null}
        >
            <NodeAliasesEditorInner
                onSave={saveAliases}
                node={node}
            />
        </Modal>
    );
}

function NodeAliasesEditorInner({onSave, node}: {
    onSave: (aliases: string[]) => void,
    node: HierarchyNode,
}) {
    const [aliases, setAliases] = useState<String[]>(node.mutableAliases());
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | undefined>();

    const updateAlias = (idx: number, newVal: string) => {
        setAliases(prev => {
            const newAliases = [...prev];
            newAliases[idx] = newVal;
            return newAliases;
        });
    }

    const addAlias = (alias: string) => {
        setAliases(prev => [...prev, alias]);
    }

    const deleteAlias = (alias: string) => {
        setAliases(prev => {
            return prev.filter(x => x !== alias);
        });
    }

    const columns: ColumnsType<String> = [
        {
            title: "Alias",
            render: (_, record, index) => {
                return (
                    <Row justify={"space-between"}>
                        <Col span={20}>
                            <EditableTextField
                                value={record}
                                editIconShown={index === hoveredRowIndex}
                                onUpdate={(newText: string) => {
                                    updateAlias(index, newText);
                                }}
                            />
                        </Col>
                        <Col span={2} offset={2}>
                            <Popconfirm
                                placement="topLeft"
                                title={`Are you sure to delete alias '${record}'?`}
                                onConfirm={() => {
                                    deleteAlias(record.valueOf());
                                }}
                                okText="Yes" cancelText="No"
                            >
                                <a style={{visibility: index === hoveredRowIndex ? "initial" : "hidden"}}>
                                    <DeleteOutlined/>
                                </a>
                            </Popconfirm>
                        </Col>
                    </Row>
                );
            }
        }
    ];

    return (
        <>
            <Space direction={"vertical"} style={{width: "100%"}}>
                <Row justify={"end"}>
                    <NewAliasEditor
                        aliases={aliases.map(x => x.valueOf())}
                        onCreate={addAlias}
                    />
                </Row>
                <Table
                    size="small"
                    pagination={false}
                    bordered={true}
                    showHeader={false}

                    dataSource={aliases}
                    columns={columns}

                    onRow={(record, rowIndex) => {
                        return {
                            onMouseEnter: event => setHoveredRowIndex(rowIndex),
                            onMouseLeave: event => setHoveredRowIndex(undefined),
                        }
                    }}
                />
                <Row justify={"end"}>
                    <Button
                        type={"primary"}
                        onClick={() => {
                            onSave(aliases.map(x => x.valueOf()));
                        }}
                    >
                        Save
                    </Button>
                </Row>
            </Space>
        </>
    );
}

function NewAliasEditor({aliases, onCreate}: {
    aliases: string[],
    onCreate: (alias: string) => void
}) {
    const [visible, setVisible] = useState(false);
    const formId = "new-alias-form";

    const formLayout = {
        labelCol: {span: 3},
        wrapperCol: {span: 21},
    };

    const aliasRules = [
        // {
        //     required: true
        // },
        {
            validator: async (_: any, target: string) => {
                if (target === "") {
                    return Promise.reject(new Error('Alias text is required!'));
                }
                if (aliases.includes(target)) {
                    return Promise.reject(new Error('Aliases must be unique!'));
                }
                return Promise.resolve();
            }
        }
    ];

    const content = (
        <div style={{width: "376px"}}>
            <Form
                id={formId}
                requiredMark={false}
                onFinish={(value) => {
                    onCreate(value.alias);
                    setVisible(false);
                }}
                initialValues={{alias: ""}}
                {...formLayout}
            >
                <Form.Item
                    label="alias"
                    name="alias"
                    style={{marginBottom: "4px"}}
                    rules={aliasRules}
                >
                    <Input id="alias-input" size="small"/>
                </Form.Item>
            </Form>
            <Row
                style={{justifyContent: "space-between"}}
            >
                <Button size="small" onClick={() => setVisible(false)} style={{width: 90}}>
                    Cancel
                </Button>
                <Button
                    form={formId}
                    key="submit" htmlType="submit"
                    type="primary" size="small" style={{width: 90}}
                >
                    OK
                </Button>
            </Row>
        </div>
    );

    return (
        <Popover
            visible={visible}
            onVisibleChange={setVisible}
            destroyTooltipOnHide={true}
            trigger={"click"}
            title={"New Alias"}
            content={content}
        >
            <Button size={"small"}>
                Add Alias
            </Button>
        </Popover>
    );
}